import { fetchConfig } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const routeRoot = () => window.Shopify?.routes?.root || '/';

const formatMoney = (cents, locale, currency) => {
  try {
    return new Intl.NumberFormat(locale || document.documentElement.lang, {
      style: 'currency',
      currency,
    }).format(Number(cents || 0) / 100);
  } catch (_) {
    return `${(Number(cents || 0) / 100).toFixed(2)} ${currency}`;
  }
};

const updateRewardState = (drawer) => {
  const rewards = drawer?.querySelector('.native-cart-rewards');
  if (!rewards) return;

  const quantity = [...drawer.querySelectorAll('input[name="updates[]"]')].reduce(
    (total, input) => total + Math.max(0, Number.parseInt(input.value || '0', 10) || 0),
    0
  );
  const thresholds = [2, 3, 4];
  const nextThreshold = thresholds.find((threshold) => quantity < threshold);
  const needed = nextThreshold ? nextThreshold - quantity : 0;
  const nextDiscount = nextThreshold === 2 ? 10 : nextThreshold === 3 ? 15 : 20;
  const message = rewards.querySelector('.native-cart-rewards__message');

  if (message) {
    message.replaceChildren();
    if (needed > 0) {
      const prefix = needed === 1
        ? rewards.dataset.rewardOne
        : rewards.dataset.rewardMany?.replace('[count]', String(needed));
      message.append(`${prefix || ''} `);
      const strong = document.createElement('strong');
      strong.textContent = `${nextDiscount}% ${rewards.dataset.rewardSuffix || ''}`.trim();
      message.append(strong);
    } else {
      message.append(`${rewards.dataset.rewardUnlocked || ''} `);
      const strong = document.createElement('strong');
      strong.textContent = `20% ${rewards.dataset.extraLabel || ''}`.trim();
      message.append(strong);
    }
  }

  const fills = quantity < 2
    ? [quantity * 50, 0, 0]
    : quantity < 3
      ? [100, 0, 0]
      : quantity < 4
        ? [100, 100, 0]
        : [100, 100, 100];

  rewards.querySelectorAll('.native-cart-rewards__segment').forEach((segment, index) => {
    segment.classList.toggle('is-complete', quantity >= thresholds[index]);
    const fill = segment.querySelector(':scope > i');
    if (fill) fill.style.width = `${fills[index]}%`;
  });
};

const fitTrustLabels = (root = document) => {
  root.querySelectorAll('.native-cart-trust').forEach((trust) => {
    const items = [...trust.querySelectorAll('.native-cart-trust__item')];
    if (!items.length || !trust.clientWidth) return;

    const maxSize = 11.5;
    const minSize = 6;
    const fits = () => items.every((item) => item.scrollWidth <= item.clientWidth + 0.5);

    trust.style.fontSize = `${maxSize}px`;
    if (fits()) return;

    let lower = minSize;
    let upper = maxSize;
    for (let step = 0; step < 7; step += 1) {
      const candidate = (lower + upper) / 2;
      trust.style.fontSize = `${candidate}px`;
      if (fits()) lower = candidate;
      else upper = candidate;
    }
    trust.style.fontSize = `${Math.floor(lower * 10) / 10}px`;
  });
};

class NativeCartUpsells extends HTMLElement {
  connectedCallback() {
    this.loadRecommendations();
  }

  loadRecommendations() {
    this.#loadRecommendations();
  }

  get #context() {
    const extras = this.closest('[data-native-cart-extras]');
    return {
      currency: extras?.dataset.currency || window.Shopify?.currency?.active || 'PLN',
      locale: extras?.dataset.locale || document.documentElement.lang,
      productIds: new Set((extras?.dataset.cartProductIds || '').split(',').filter(Boolean)),
      addLabel: extras?.dataset.addLabel || 'Add',
      addingLabel: extras?.dataset.addingLabel || 'Adding…',
      chooseLabel: extras?.dataset.chooseLabel || 'Choose',
      errorLabel: extras?.dataset.errorLabel || 'The cart could not be updated. Please try again.',
    };
  }

  async #loadRecommendations() {
    const productId = this.dataset.productId;
    const track = this.querySelector('[data-upsell-track]');
    if (!productId || !track || track.children.length || this.dataset.loading === 'true') return;

    this.dataset.loading = 'true';
    try {
      const response = await fetch(
        `${routeRoot()}recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=8&intent=related`,
        { headers: { Accept: 'application/json' } }
      );
      if (!response.ok) throw new Error(`Recommendations failed: ${response.status}`);
      const data = await response.json();
      const context = this.#context;
      const products = (data.products || []).filter(
        (product) => product.available && !context.productIds.has(String(product.id))
      );

      if (!products.length) {
        this.hidden = true;
        return;
      }

      track.replaceChildren(...products.map((product) => this.#createCard(product, context)));
      this.hidden = false;
    } catch (_) {
      if (!track.children.length) this.hidden = true;
    } finally {
      delete this.dataset.loading;
    }
  }

  #createCard(product, context) {
    const availableVariants = (product.variants || []).filter((variant) => variant.available);
    const card = document.createElement('article');
    card.className = 'native-cart-upsell';

    const imageLink = document.createElement('a');
    imageLink.className = 'native-cart-upsell__image';
    imageLink.href = `${routeRoot()}products/${product.handle}`;
    const image = document.createElement('img');
    image.src = product.featured_image || product.images?.[0] || '';
    image.alt = product.title;
    image.loading = 'lazy';
    imageLink.append(image);

    const body = document.createElement('div');
    body.className = 'native-cart-upsell__body';
    const title = document.createElement('a');
    title.className = 'native-cart-upsell__title';
    title.href = imageLink.href;
    title.textContent = product.title;

    const price = document.createElement('div');
    price.className = 'native-cart-upsell__price';

    const select = document.createElement('select');
    select.className = 'native-cart-upsell__select';
    select.setAttribute('aria-label', `${context.chooseLabel} ${product.title}`);
    availableVariants.forEach((variant) => {
      const option = document.createElement('option');
      option.value = String(variant.id);
      option.textContent = variant.title === 'Default Title' ? product.title : variant.title;
      option.dataset.price = String(variant.price ?? product.price ?? 0);
      option.dataset.compareAt = String(variant.compare_at_price || 0);
      select.append(option);
    });

    const updatePrice = () => {
      const option = select.selectedOptions[0];
      const current = Number(option?.dataset.price || product.price || 0);
      const compareAt = Number(option?.dataset.compareAt || 0);
      price.replaceChildren();
      if (compareAt > current) {
        const oldPrice = document.createElement('s');
        oldPrice.textContent = formatMoney(compareAt, context.locale, context.currency);
        price.append(oldPrice);
      }
      const currentPrice = document.createElement('strong');
      currentPrice.textContent = formatMoney(current, context.locale, context.currency);
      price.append(currentPrice);
    };
    select.addEventListener('change', updatePrice);
    updatePrice();

    const add = document.createElement('button');
    add.type = 'button';
    add.className = 'native-cart-upsell__add';
    add.textContent = context.addLabel;
    add.addEventListener('click', () => this.#addVariant(select.value, product, add, context));

    const controls = document.createElement('div');
    controls.className = 'native-cart-upsell__controls';
    if (availableVariants.length > 1) controls.append(select);
    controls.append(add);
    body.append(title, price, controls);
    card.append(imageLink, body);
    return card;
  }

  async #addVariant(variantId, product, button, context) {
    if (!variantId || button.disabled) return;

    const errorOutput = this.closest('[data-native-cart-extras]')?.querySelector('[data-native-cart-error]');
    if (errorOutput) errorOutput.textContent = '';
    button.disabled = true;
    button.textContent = context.addingLabel;

    const cartItemsComponents = [...document.querySelectorAll('cart-items-component')];
    const sectionIds = cartItemsComponents.map((component) => component.dataset.sectionId).filter(Boolean);
    const formData = new FormData();
    formData.set('id', variantId);
    formData.set('quantity', '1');
    formData.set('sections', sectionIds.join(','));
    formData.set('sections_url', window.location.pathname);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    let addCommitted = false;
    try {
      const config = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      });
      const data = await response.json();
      if (!response.ok || data.status || data.errors) {
        throw new Error(data.description || data.message || 'Add to cart failed');
      }
      addCommitted = true;

      const cartItems = cartItemsComponents[0];
      let ajaxCart;
      if (cartItems) {
        try {
          ajaxCart = await cartItems.fetchCartData();
        } catch (_) {
          ajaxCart = null;
        }
      }
      if (!ajaxCart) {
        const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        });
        if (!cartResponse.ok) throw new Error(`Cart refresh failed: ${cartResponse.status}`);
        ajaxCart = await cartResponse.json();
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'native-cart-upsell',
          sourceId: product.id,
          itemCount: 1,
          productId: product.id,
          sections: data.sections,
          didError: false,
        },
      });
    } catch (error) {
      deferred.reject(error);

      // Shopify has already committed the add at this point. Never show a retry
      // action that could add the same product twice; reload once so the native
      // cart renders Shopify's authoritative state.
      if (addCommitted) {
        button.textContent = Theme.translations?.added || context.addLabel;
        window.setTimeout(() => window.location.reload(), 100);
        return;
      }

      button.disabled = false;
      button.textContent = context.addLabel;
      if (errorOutput) {
        errorOutput.textContent = context.errorLabel;
        window.setTimeout(() => {
          if (errorOutput.textContent === context.errorLabel) errorOutput.textContent = '';
        }, 5000);
      }
      this.dispatchEvent(
        new CartErrorEvent({
          error: error?.message || 'Add to cart failed',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
    }
  }
}

if (!customElements.get('native-cart-upsells')) {
  customElements.define('native-cart-upsells', NativeCartUpsells);
}

const refreshUpsells = () => {
  document.querySelectorAll('native-cart-upsells').forEach((element) => element.loadRecommendations?.());
};

const refreshNativeCartUi = () => {
  refreshUpsells();
  fitTrustLabels();
};

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise
    ?.then(() => window.setTimeout(refreshUpsells, 250))
    .catch(() => {});
});

document.addEventListener(ThemeEvents.quantitySelectorUpdate, (event) => {
  const drawer = event.target.closest?.('#cart-drawer');
  if (drawer) window.requestAnimationFrame(() => updateRewardState(drawer));
});

const cartSection = document.getElementById('shopify-section-cart-drawer-section');
if (cartSection) {
  let refreshTimer;
  new MutationObserver(() => {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(refreshNativeCartUi, 50);
  }).observe(cartSection, { childList: true, subtree: true });
}

const cartDrawer = document.getElementById('cart-drawer');
if (cartDrawer) {
  new MutationObserver(() => {
    if (cartDrawer.hasAttribute('open')) {
      window.requestAnimationFrame(() => {
        fitTrustLabels(cartDrawer);
        window.setTimeout(() => fitTrustLabels(cartDrawer), 250);
      });
    }
  }).observe(cartDrawer, { attributes: true, attributeFilter: ['open'] });
}

fitTrustLabels();
document.fonts?.ready.then(() => fitTrustLabels()).catch(() => {});
window.addEventListener('resize', () => window.requestAnimationFrame(() => fitTrustLabels()));
